import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { OnboardingStepDefinition } from '../../model/types';
import OnboardingStepper from '../stepper/OnboardingStepper';

interface OnboardingModalProps {
  stepDefinition: OnboardingStepDefinition;
  steps: OnboardingStepDefinition[];
  currentStep: number;
  completedSteps: number[];
  onStepSelect: (step: OnboardingStepDefinition['step']) => void;
  onSaveAndExit: () => void;
  children?: ReactNode;
  error?: string | null;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  stepDefinition,
  steps,
  currentStep,
  completedSteps,
  onStepSelect,
  onSaveAndExit,
  children,
  error,
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('he') || i18n.language.startsWith('ar');

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[14px]"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-[28px] border border-white/20 bg-white shadow-2xl dark:border-neutral-700 dark:bg-neutral-900"
      >
        <div className="relative overflow-hidden border-b border-neutral-200 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-500 px-4 py-5 text-white sm:px-6 sm:py-6 dark:border-neutral-800">
          <div className={`pointer-events-none absolute inset-y-0 w-80 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.22),transparent_62%)] ${isRTL ? 'left-0' : 'right-0'}`} />
          <div className="pointer-events-none absolute -bottom-10 left-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className={`relative z-10 flex flex-col gap-4 ${isRTL ? 'text-right' : 'text-left'} lg:flex-row lg:items-start lg:justify-between`}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="min-w-0 max-w-3xl"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/75">
                Guided setup
              </p>
              <h2 className="mt-3 break-words text-2xl font-semibold sm:text-3xl">{stepDefinition.title}</h2>
              <p className="mt-2 max-w-2xl whitespace-pre-line text-sm text-white/85">{stepDefinition.description}</p>
            </motion.div>

            <div className={`flex shrink-0 ${isRTL ? 'justify-start lg:justify-end' : 'justify-end'}`}>
              <motion.button
                type="button"
                onClick={onSaveAndExit}
                whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-white/15"
              >
                <XMarkIcon className="h-4 w-4" />
                Save and exit
              </motion.button>
            </div>
          </div>
        </div>

        <div className="border-b border-neutral-200 bg-neutral-50/90 px-4 py-4 sm:px-6 sm:py-5 dark:border-neutral-800 dark:bg-neutral-950">
          <OnboardingStepper
            steps={steps}
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepSelect={onStepSelect}
          />
        </div>

        <div className="flex-1 overflow-y-auto bg-gradient-to-b from-neutral-50 to-white px-4 py-5 sm:px-6 sm:py-6 dark:from-neutral-950 dark:to-neutral-900">
          {error ? (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className={`mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/50 dark:text-rose-200 ${isRTL ? 'text-right' : 'text-left'}`}
            >
              {error}
            </motion.div>
          ) : null}
          {children}
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingModal;
